import { useMemo, createElement } from 'react';
import { useSelector } from 'react-redux';
import _cloneDeep from 'lodash/cloneDeep';
import ReactDOM from 'react-dom';

import { getLocalAuth } from '../../utils/environmentUtils';
import ID from './UUID';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import NumbersIcon from '@mui/icons-material/Numbers';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import CalculateIcon from '@mui/icons-material/Calculate';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TodayIcon from '@mui/icons-material/Today';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import LinkIcon from '@mui/icons-material/Link';
import LinkedCameraIcon from '@mui/icons-material/LinkedCamera';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TitleIcon from '@mui/icons-material/Title';
import SegmentIcon from '@mui/icons-material/Segment';
import LabelIcon from '@mui/icons-material/Label';
import StyleIcon from '@mui/icons-material/Style';
import LanguageIcon from '@mui/icons-material/Language';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import DataArrayIcon from '@mui/icons-material/DataArray';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { createAndDownloadFile } from '../../utils/utilFunctions';

export const makeFieldCopy = (field) => {
	const fieldCopy = _cloneDeep(field);

	const newFieldName = fieldCopy?.field_name
		? `${fieldCopy.field_name.split('_').slice(0, -1).join('_')}_${ID.uuid()}`
		: `${fieldCopy?.element?.toLowerCase() || ''}_${ID.uuid()}`;

	if (
		fieldCopy.key === 'MUI_RadioButton' ||
		fieldCopy.key === 'MUI_Dropdown' ||
		fieldCopy.key === 'MUI_Checkbox'
	) {
		fieldCopy.options = fieldCopy.options.map((item) => ({
			...item,
			key: `option_${ID.uuid()}`
		}));
	}
	const newID = ID.uuid();

	const FieldCopy = {
		...fieldCopy,
		field_name: newFieldName,
		field_id: newID,
		id: newID
	};
	return FieldCopy;
};

export const makeGroupCopy = (field, entireForm) => {
	const mixedFormSteps = entireForm?.flat(1);
	const startID = field?.id;
	const startIndex = mixedFormSteps?.findIndex((e) => e.id === startID);
	const endIndex = mixedFormSteps?.findIndex(
		(e, index) => e.key === 'MUI_GroupingEnd' && index > startIndex
	);

	const slicedElements = mixedFormSteps.slice(startIndex, endIndex + 1);
	const groupCopy = slicedElements?.map((e) => makeFieldCopy(e));

	return groupCopy?.length > 0 ? groupCopy : null;
};

export const stripTextHTML = (text) => {
	let tmp = document.createElement('DIV');
	tmp.innerHTML = text;
	const label = tmp.textContent || tmp.innerText || '';
	return label;
};

export function stringToHTML(htmlString) {
	const parser = new DOMParser();
	const doc = parser.parseFromString(htmlString, 'text/html');
	const elements = Array.from(doc.body.childNodes);

	// Check if the string is plain text without HTML elements
	if (elements.length === 0 || (elements.length === 1 && elements[0].nodeType === Node.TEXT_NODE)) {
		return <span key="0">{htmlString}</span>;
	}

	// Handle cases where there are HTML tags
	return elements.map((element, index) => {
		if (element.nodeType === Node.ELEMENT_NODE) {
			const TagName = element.tagName.toLowerCase();
			return createElement(TagName, {
				key: index,
				dangerouslySetInnerHTML: { __html: element.innerHTML }
			});
		}
		return null;
	});
}

export const checkForEmptyForms = (form) => {
	let isNull = false;
	form?.forEach((e) => {
		isNull = e?.length === 0 ? true : isNull;
	});
	return isNull;
};

export const checkMissingGroupings = (form) => {
	const errStart =
		'The Grouping Start element must be included in this form and must come before the Grouping End element.';
	const errEnd =
		'The Grouping End element must be included in this form and must appear after the Grouping Start element.';

	let balance = 0;
	let seenStart = false;
	let seenEnd = false;

	for (const page of form || []) {
		for (const field of page || []) {
			if (field?.key === 'MUI_GroupingStart') {
				balance += 1;
				seenStart = true;
			} else if (field?.key === 'MUI_GroupingEnd') {
				seenEnd = true;
				if (balance === 0) {
					return { isValid: false, errorMessage: errStart };
				}
				balance -= 1;
			}
		}
	}

	if (seenStart && !seenEnd) return { isValid: false, errorMessage: errEnd };
	if (seenEnd && !seenStart) return { isValid: false, errorMessage: errStart };
	if (balance > 0) return { isValid: false, errorMessage: errEnd };

	return { isValid: true, errorMessage: null };
};

export const validateMinAndMaxDate = (form) => {
	const datePicker = form[0]?.find((e) => e.key === 'MUI_DatePicker');
	const { min_date: minDate, max_date: maxDate } = datePicker || {};
	return minDate !== undefined && maxDate !== undefined
		? new Date(maxDate) < new Date(minDate)
		: false;
};

export const validateMinAndMaxValues = (form, isReviewForm) => {
	if (!isReviewForm) {
		return false;
	}
	const numberInput = form[0]?.find((e) => e.key === 'MUI_NumberInput');
	const { min_value: minValue, max_value: maxValue, description: description } = numberInput || {};
	return (
		(minValue === undefined || maxValue === undefined || minValue === '' || maxValue === '') &&
		description === 'total_score'
	);
};

export const elementIcons = {
	LineBreak: <HorizontalRuleIcon />,
	MUI_GroupingStart: <DataArrayIcon />,
	MUI_GroupingEnd: <DataArrayIcon />,
	TwoColumnRow: <ViewComfyIcon />,
	ThreeColumnRow: <ViewComfyIcon />,
	MultiColumnRow: <ViewComfyIcon />,
	Instructions: <ErrorOutlineIcon />,
	Header: <TitleIcon />,
	Paragraph: <SegmentIcon />,
	Label: <LabelIcon />,
	MUI_Tags: <StyleIcon />,
	HyperLink: <LanguageIcon />,
	MUI_Input: <ExitToAppIcon />,
	MUI_TextArea: <ExitToAppIcon />,
	MUI_Email: <AlternateEmailIcon />,
	MUI_PhoneNumber: <LocalPhoneIcon />,
	MUI_NumberInput: <NumbersIcon />,
	MUI_Dropdown: <ExpandCircleDownOutlinedIcon />,
	MUI_Checkbox: <CheckBoxOutlinedIcon />,
	MUI_RadioButton: <RadioButtonCheckedIcon />,
	StatePicker: <LocationCityIcon />,
	MUI_Calculator: <CalculateIcon />,
	MUI_DatePicker: <TodayIcon />,
	MUI_StartEndDate: <CalendarMonthIcon />,
	MUI_Rating: <StarHalfIcon />,
	MUI_LinkInput: <LinkIcon />,
	Image: <ImageOutlinedIcon />,
	MUI_FileUpload: <CloudUploadIcon />,
	MUI_FileAttachment: <DownloadIcon />,
	Signature: <LinkedCameraIcon />
};

export const getElementIcon = (key) => {
	return elementIcons[key];
};

const sendImageCaptureToServer = (apiToken) => async (filename, fileData) => {
	const apiUrl = process.env.REACT_APP_BASE_URL;
	const formData = new FormData();
	formData.append('file', fileData, filename);

	try {
		const response = await fetch(`${apiUrl}form/fields/upload_file/`, {
			method: 'POST',
			body: fileData,
			headers: {
				Authorization: `Bearer ${apiToken}`,
				'Content-Disposition': 'attachment; filename=file',
				filename: filename
			}
		});

		const data = await response.text();
		return data;
	} catch (error) {
		console.error('Error:', error);
		throw error;
	}
};

const sendFileToServer = (apiToken) => async (filename, fileData) => {
	const apiUrl = process.env.REACT_APP_BASE_URL;

	let path = null;

	try {
		const response = await fetch(`${apiUrl}form/fields/upload_file/`, {
			method: 'POST',
			body: new Blob([fileData]),
			headers: {
				Authorization: `Bearer ${apiToken}`,
				'Content-Disposition': 'attachment; filename=file',
				filename: filename
			}
		});

		const contentType = response.headers.get('content-type');
		if (contentType.includes('json')) {
			path = await response.json();
		}
		if (contentType.includes('text')) {
			path = await response.text();
		}
		return path;
	} catch (error) {
		console.error('Error uploading file:', error);
		return path;
	}
};

const sanitizeFilename = (filename) => {
	return String(filename).trim();
};

const downloadFile = (apiToken) => (filePath) => {
	const apiUrl = process.env.REACT_APP_BASE_URL;
	const sanitizedFilename = sanitizeFilename(filePath);
	fetch(`${apiUrl}form/fields/download_file/`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${apiToken}`,
			filename: sanitizedFilename
		}
	})
		.then((response) => response.blob())
		.then((blob) => createAndDownloadFile(blob, sanitizedFilename))
		.catch((error) => {
			console.error('Error:', error);
		});
};

const displayImage = (apiToken) => async (filePath) => {
	const apiUrl = process.env.REACT_APP_BASE_URL;
	const filename = typeof filePath === 'object' ? JSON.stringify(filePath) : filePath;
	let photoURL = '';

	await fetch(`${apiUrl}form/fields/download_file/`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${apiToken}`,
			filename
		}
	})
		.then((response) => {
			return response.blob(); // Convert response to Blob
		})
		.then((blob) => {
			// Create a temporary URL for the Blob
			const blobUrl = URL.createObjectURL(blob);
			photoURL = blobUrl;
		})
		.catch((error) => {
			console.error('Error:', error);
		});

	return photoURL;
};

export function useFileUtils() {
	const accountSource = useSelector((state) => state?.login?.accountSource);
	const apiToken = getLocalAuth(accountSource)
		?.access_token.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');

	const memoFns = useMemo(
		() => ({
			sendFileToServer: sendFileToServer(apiToken),
			displayImage: displayImage(apiToken),
			downloadFile: downloadFile(apiToken),
			sendImageCaptureToServer: sendImageCaptureToServer(apiToken)
		}),
		[apiToken]
	);

	return memoFns;
}

export const CharacterCounter = ({ isActive, currentCount, maxCount }) => {
	if (!maxCount || !isActive) return null;
	const isAtLimit = currentCount >= maxCount;
	return (
		<span className={`counter ${isAtLimit ? 'has-limit' : 'no-limit'}`} data-name="counter">
			{isAtLimit && <span className="counter-alert">Max reached!</span>}
			<span className="counter-value">
				{currentCount}/{maxCount}
			</span>
		</span>
	);
};

export const capitalizeFirstLetter = (string) => {
	//capitalize the first letter
	return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const calculateTotalPoints = (formSchema) => {
	let totalPoints = 0;
	const formSchemaData = {};
	if (!formSchema) {
		return totalPoints;
	}
	formSchema.forEach((e) => {
		if (e && 'description' in e && e.description === 'total_score') {
			formSchemaData[e.field_name] = 1;
			if (e.key === 'MUI_NumberInput' && e.max_value) {
				totalPoints += parseInt(e.max_value);
			} else if (e.key === 'MUI_Checkbox') {
				e.options?.forEach((option) => {
					let value = parseInt(option.value);
					if (!isNaN(value)) {
						totalPoints += value;
					}
				});
			} else if (['MUI_RadioButton', 'StatePicker', 'MUI_Dropdown'].includes(e.key)) {
				let maxValue = 0;
				e.options?.forEach((option) => {
					let value = parseInt(option.value);
					if (!isNaN(value)) {
						maxValue = Math.max(maxValue, value);
					}
				});
				totalPoints += maxValue;
			}
		}
	});

	return totalPoints;
};

export const calculateUserScore = (formSchema, formAnswers) => {
	let userScore = 0;
	const formSchemaData = {};
	formSchema.forEach((e) => {
		if (e && 'description' in e && e.description === 'total_score') {
			formSchemaData[e.field_name] = 1;
		}
	});
	formAnswers.forEach((e) => {
		if (e && formSchemaData.hasOwnProperty(e.name)) {
			if (e.name.includes('mui_number_')) {
				let value = parseInt(e.value);
				if (!isNaN(value)) {
					userScore += value;
				}
			} else if (e.name.includes('mui_radiobutton_')) {
				const el = document.getElementsByName(`${e?.name}`)?.[0];
				if (el) {
					const elem = ReactDOM.findDOMNode(el);
					for (let child of elem.children[1].children[0].children[0].children) {
						if ([...child.children[0].children[0].classList].includes('Mui-checked')) {
							let value = child.children[0].children[0].children[0].getAttribute('data-value');
							value = parseInt(value);
							if (!isNaN(value)) {
								userScore += value;
							}
						}
					}
				}
			} else if (e.name.includes('mui_checkbox_')) {
				let fSchema = formSchema?.find((x) => x?.field_name === e?.name);
				e?.value?.forEach((x) => {
					let value = fSchema?.options?.find((i) => i?.key === x)?.value;
					value = parseInt(value);
					if (!isNaN(value)) {
						userScore += value;
					}
				});
			} else if (e.name.includes('mui_dropdown_')) {
				let fSchema = formSchema?.find((x) => x?.field_name === e?.name);
				let value = fSchema?.options?.find((i) => i?.key === e?.value)?.value;
				value = parseInt(value);
				if (!isNaN(value)) {
					userScore += value;
				}
			} else if (e.name.includes('mui_statepicker_')) {
				let value = parseInt(e.value);
				if (!isNaN(value)) {
					userScore += value;
				}
			}
		}
	});

	return userScore;
};

export const parseFieldProperty = (prop) => {
	if (prop === 'label') return 'Label';
	if (prop === 'limit_value') return 'Character Limit';
	if (prop === 'help_message') return 'Inline Help';
	if (prop === 'second_label') return 'Link Label';
	if (prop === 'src') return 'Link To';
	if (prop === 'width') return 'Width';
	if (prop === 'height') return 'Height';
	if (prop === 'min_value') return 'Minimum Value';
	if (prop === 'max_value') return 'Maximum Value';
};

const mapPrintOptionsForApi = ({ pageBreakBefore, ...field }) => {
	const mapped = { ...field };
	if (pageBreakBefore !== undefined) {
		mapped.page_break_before = pageBreakBefore;
	}
	return mapped;
};

const mapPrintOptionsFromApi = ({ page_break_before, ...field }) => {
	const mapped = { ...field };
	if (page_break_before != null) {
		mapped.pageBreakBefore = !!page_break_before;
	}
	return mapped;
};

export const mapFormSchemaPrintOptionsForApi = (schema) => schema?.map(mapPrintOptionsForApi) ?? [];
export const mapFormSchemaPrintOptionsFromApi = (schema) =>
	schema?.map(mapPrintOptionsFromApi) ?? [];

export const getTemplateLinkAndText = (templateData) => {
	const label = templateData?.label || 'Template';
	const formPrefix = templateData?.form_type === 'application' ? 'baft' : 'brft';
	const uniqueIdentifier = templateData?.unique_identifier || 'N/A';
	const formId = templateData?.form_id || '';
	const formType = templateData?.form_type === 'application' ? 'Application Form' : 'Review Form';
	const link =
		templateData?.form_type === 'application'
			? `/templates/base-application-forms?formBuilder=application&formId=${formId}`
			: templateData?.form_type === 'review'
			? `/templates/base-review-forms?formBuilder=review&formId=${formId}`
			: '#';

	const linkText = `${label} [${formPrefix}-${uniqueIdentifier}]`;

	return { link, linkText, formType };
};
